/*
 * Pure schema.org JSON-LD builders.
 *
 * CommonJS so both the React <…Schema> components and the build-time prerender
 * (scripts/inject-route-meta.js) call the exact same functions — there is no
 * second copy of the schema shapes to drift out of sync.
 *
 * Every builder returns a plain object ready for JSON.stringify. Cross-node
 * references use @id pointing at the Organization emitted by buildSiteGraph(),
 * which is rendered site-wide, so the @id always resolves on the page.
 */
const {
  BASE_URL,
  SITE_NAME,
  AUTHOR,
  LOGO_URL,
  OG_IMAGE_URL,
  DEFAULT_DESC,
} = require("./seoConfig");

const ORG_ID = `${BASE_URL}/#organization`;
const WEBSITE_ID = `${BASE_URL}/#website`;

function absUrl(pathOrUrl) {
  if (!pathOrUrl) return BASE_URL;
  return /^https?:\/\//.test(pathOrUrl) ? pathOrUrl : `${BASE_URL}${pathOrUrl}`;
}

// Organization + WebSite. Rendered once, site-wide (mounted in App.js and
// baked into every prerendered route).
function buildSiteGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": ORG_ID,
        name: SITE_NAME,
        url: BASE_URL,
        logo: { "@type": "ImageObject", url: LOGO_URL },
        founder: { "@type": "Person", name: AUTHOR },
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        name: SITE_NAME,
        url: BASE_URL,
        publisher: { "@id": ORG_ID },
        inLanguage: "en-GB",
      },
    ],
  };
}

// Article / analysis / guide pages. headline is the only field Google treats
// as required; everything else is recommended and emitted when supplied.
function buildArticleSchema({
  headline,
  description,
  path,
  datePublished,
  dateModified,
  author,
  image,
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description: description || DEFAULT_DESC,
    url: absUrl(path),
    mainEntityOfPage: { "@type": "WebPage", "@id": absUrl(path) },
    author: { "@type": "Person", name: author || AUTHOR },
    publisher: { "@id": ORG_ID },
    image: absUrl(image || OG_IMAGE_URL),
  };
  if (datePublished) schema.datePublished = datePublished;
  if (dateModified) schema.dateModified = dateModified;
  return schema;
}

// Interactive tool pages (/tools/*). Free web apps.
function buildToolSchema({ name, description, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description: description || DEFAULT_DESC,
    url: absUrl(path),
    applicationCategory: "SportsApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
    publisher: { "@id": ORG_ID },
  };
}

module.exports = {
  absUrl,
  buildSiteGraph,
  buildArticleSchema,
  buildToolSchema,
};
